import { ModalGalleryComponent } from './../modal-gallery/modal-gallery.component';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { AfterViewInit, Component, ElementRef, HostBinding, Inject, Input, OnInit, PLATFORM_ID, QueryList, ViewChild, ViewChildren, TemplateRef, OnDestroy } from '@angular/core';
import { CarouselComponent, SlidesOutputData, CarouselModule } from 'ngx-owl-carousel-o';
import { OwlCarouselOConfig } from 'ngx-owl-carousel-o/lib/carousel/owl-carousel-o-config';
import { PhotoSwipeItem, PhotoSwipeService, PhotoSwipeThumbBounds } from '@services/photo-swipe.service';
import { DirectionService } from '@services/direction.service';
import { isPlatformBrowser, NgIf, NgFor } from '@angular/common';
import { ProductLayout } from '../../../pages/page-product/product/product.component';
import * as _ from 'lodash';
import { ClickDirective } from '../../directives/click.directive';
import { OwlPreventClickDirective } from '../../directives/owl-prevent-click.directive';
import { Title } from '@angular/platform-browser';

export interface ProductGalleryItem {
    id: any;
    image: string;
}

@Component({
    selector: 'app-product-gallery-new',
    templateUrl: './product-gallery-new.component.html',
    styleUrls: ['./product-gallery-new.component.scss'],
    standalone: true,
    imports: [NgIf, CarouselModule, OwlPreventClickDirective, NgFor, ClickDirective]
})
export class ProductGalleryNewComponent implements OnInit, OnDestroy, AfterViewInit {

    constructor(
        @Inject(PLATFORM_ID) private platformId: any,
        private photoSwipe: PhotoSwipeService,
        public direction: DirectionService,
        private modalService: BsModalService,
        public titleService: Title,

    ) { }

    items: ProductGalleryItem[] = [];

    currentItem: ProductGalleryItem = null;

    carouselOptions: Partial<OwlCarouselOConfig> = {
        dots: true,
        nav: false,
        autoplay: false,
        responsive: {
            0: { items: 1 }
        },
        // items: 1,
        rtl: this.direction.isRTL()
    };

    thumbnailsCarouselOptions: Partial<OwlCarouselOConfig> = {
        dots: true,
        autoplay: false,
        margin: 10,
        items: 5,
        responsive: {
            480: { items: 5 },
            200: { items: 4 },
            0: { items: 3 }
        },
        rtl: this.direction.isRTL()
    };

    @Input() productLayout: ProductLayout;
    @Input() imageRatio: 'ratio-1by1' | 'ratio-16by9' = 'ratio-1by1';

    @Input() set images(images: any) {
        let productImages = _.map(images, (data, id) => {
            return { id: id, image: data.url }
        });
        this.items = productImages;
        this.currentItem = this.items[0] || null;
    }

    @HostBinding('class.product-gallery') classProductGallery = true;
    @ViewChild('featuredCarousel', { read: CarouselComponent }) featuredCarousel: CarouselComponent;
    @ViewChild('thumbnailsCarousel', { read: CarouselComponent }) thumbnailsCarousel: CarouselComponent;
    @ViewChildren('imageElement', { read: ElementRef }) imageElements: QueryList<ElementRef>;

    image = 'assets/images/nologo.png';

    ngOnDestroy(): void {
        this.gallery?.unsubscribe();
    }

    ngOnInit(): void {
    }

    ngAfterViewInit(): void {
        if (isPlatformBrowser(this.platformId)) {
            if (this.productLayout !== 'quickview') {
                this.photoSwipe.load().subscribe();
            }

            window.dispatchEvent(new Event('resize'));
        }
    }

    featuredCarouselTranslated(event: SlidesOutputData): void {
        if (event.slides.length) {
            const activeImageId = event.slides[0].id;

            this.currentItem = this.items.find(x => x.id === activeImageId) || this.items[0] || null;

            if (!this.thumbnailsCarousel?.slidesData?.find(slide => slide.id === activeImageId && slide.isActive)) {
                this.thumbnailsCarousel?.to(activeImageId);
            }
        }
    }

    getThumbnailsOptions() {
        if (this.imageRatio == 'ratio-16by9') {
            this.thumbnailsCarouselOptions.items = 10;
            this.thumbnailsCarouselOptions.responsive = {
                480: { items: 8 },
                200: { items: 4 },
                0: { items: 3 }
            };
        }

        return this.thumbnailsCarouselOptions;
    }

    getDirDependentIndex(index) {
        // we need to invert index id direction === 'rtl' because photoswipe do not support rtl
        if (this.direction.isRTL()) {
            return this.items.length - 1 - index;
        }

        return index;
    }

    onFeaturedImageClick(event: MouseEvent, image: any): void {
        event.preventDefault();
        if (this.productLayout !== 'quickview') {
            event.preventDefault();

            this.openPhotoSwipe(image);
        }
    }

    onThumbnailImageClick(item: ProductGalleryItem): void {
        this.featuredCarousel.to(item.id);
        this.currentItem = item;
    }

    openPhotoSwipe(item: ProductGalleryItem): void {
        if (!item) {
            return;
        }

        const imageElements = this.imageElements.map(x => x.nativeElement);
        const images: PhotoSwipeItem[] = this.items.map((eachItem, i) => {
            const tag: HTMLImageElement = imageElements[i];
            const width = parseFloat(tag.dataset.width) || tag.naturalWidth;
            const height = parseFloat(tag.dataset.height) || tag.naturalHeight;

            return {
                src: eachItem.image,
                msrc: eachItem.image,
                w: width,
                h: height,
            };
        });

        if (this.direction.isRTL()) {
            images.reverse();
        }

        // noinspection JSUnusedGlobalSymbols
        const options = {
            getThumbBoundsFn: index => this.getThumbBounds(index),
            index: this.getDirDependentIndex(this.items.indexOf(item)),
            bgOpacity: .9,
            history: false,
        };

        this.gallery = this.photoSwipe.open(images, options).subscribe(galleryRef => {
            galleryRef.listen('beforeChange', () => {
                this.featuredCarousel.to(this.items[this.getDirDependentIndex(galleryRef.getCurrentIndex())].id);
            });

        });
    }
    gallery;

    getThumbBounds(index: number): PhotoSwipeThumbBounds {
        const imageElements = this.imageElements.toArray();
        const dirDependentIndex = this.getDirDependentIndex(index);

        if (!imageElements[dirDependentIndex]) {
            return null;
        }

        const tag = imageElements[dirDependentIndex].nativeElement;
        const width = tag.naturalWidth;
        const height = tag.naturalHeight;
        const rect = tag.getBoundingClientRect();
        const ration = Math.min(rect.width / width, rect.height / height);
        const fitWidth = width * ration;
        const fitHeight = height * ration;

        return {
            x: rect.left + (rect.width - fitWidth) / 2 + window.pageXOffset,
            y: rect.top + (rect.height - fitHeight) / 2 + window.pageYOffset,
            w: fitWidth,
        };
    }

    selectedImage;

    bsModalRef: BsModalRef;
    @ViewChild('modal', { read: TemplateRef }) template: TemplateRef<any>;
    //@ViewChild('owlCar', { read: CarouselComponent }) owlCar: CarouselComponent;
    owlCar: CarouselComponent;
    changeimage(image: string) {
        this.selectedImage = image;
    }

    showGallery(event: MouseEvent, image: any, element, index): void {
        event.preventDefault();
        /* const width = element.naturalWidth;
        const height = element.naturalHeight;
        const rect = element.getBoundingClientRect();
        const ration = Math.min(rect.width / width, rect.height / height);
        const fitWidth = width * ration; */
        this.bsModalRef = this.modalService.show(
            //this.template,
            ModalGalleryComponent,
            {
                initialState: {
                    selectedImage: image.image,
                    items: this.items,
                    imageRatio: this.imageRatio,
                    //width: fitWidth,
                    selectedIndex: index
                },
                class: 'modal-lg card-radius-06 w-10/12 h-10/12'
            });

        this.modalService.onShown.subscribe((shown) => {
            element
        })
    }
}
